import fp from 'lodash/fp';
import React from 'react';
import { connect } from 'react-redux';
import {
  lifecycle,
  withHandlers,
  withProps,
  withStateHandlers
} from 'recompose';
import { bindActionCreators, compose } from 'redux';
import { getUserIndustryGoals as getBusinessIndustryGoals } from '../../../services';
import {
  getUserCpdActivityColumns,
  mapUserCpdActivity
} from '../../../services/cpd-activity';
import {
  downloadBlobStreamFile,
  getBlobStream,
  getCsvStream,
  writeDataToCsvStream
} from '../../../utils';
import { LoadingButton } from '../../components';
import { Icon } from '../../components';

export const createDownloadBusinessActivityButton = ({
  getBusinessActivities,
  cleanBusinessActivities,
  stateSelectors
}) => {
  const DownloadButton = ({
    isDownloadingBusinessActivities,
    downloadingProgress,
    toggleDownloading,
    buttonText,
    downloadFor
  }) => {
    const isOffsetDownload =
      downloadFor === 'businessWithYearsOffset' ||
      downloadFor === 'adminWithYearsOffset';

    const isAdmin = downloadFor === 'admin';

    const buttonClass = isAdmin
      ? 'no-margin'
      : isOffsetDownload
      ? 'large full pull-down-single download-business-activity-btn'
      : '';

    const renderButtonText = isOffsetDownload ? (
      <span>
        <Icon
          iconSet="glyphicon"
          icon="download-alt"
          className="space-right"
        />
        {buttonText}
      </span>
    ) : (
      buttonText
    );

    const progressText = `${(100 * downloadingProgress).toFixed(0)}%`;

    return (
      <div>
        <LoadingButton
          {...(isAdmin ? { bsStyle: 'plain' } : {})}
          className={buttonClass}
          text={renderButtonText}
          loading={isDownloadingBusinessActivities}
          loadingText={isOffsetDownload && isDownloadingBusinessActivities ? progressText : null}
          onClick={toggleDownloading}
        />
        {isAdmin && isDownloadingBusinessActivities ? progressText : null}
      </div>
    );
  };

  const mapStateToProps = (state) => ({
    ...stateSelectors(state)
  });

  const mapDispatchToProps = (dispatch) => ({
    getBusinessActivities: bindActionCreators(getBusinessActivities, dispatch),
    cleanBusinessActivities: bindActionCreators(
      cleanBusinessActivities,
      dispatch
    )
  });

  return compose(
    connect(mapStateToProps, mapDispatchToProps),
    withProps(({ trainingPlans, industriesWithCpdCategorys, downloadPeriodText, business }) => {
      const industryCpdCategorys = fp.flow(
        fp.map('industry'),
        fp.map(({ id }) =>
          fp.flow(
            fp.find(({ industry }) => industry.id === id),
            fp.get('cpdCategorys')
          )(industriesWithCpdCategorys)
        ),
        fp.flatten
      )(trainingPlans);

      const businessIndustryGoals = getBusinessIndustryGoals(
        { trainingPlans },
        industriesWithCpdCategorys
      );
      const downloadPeriod = downloadPeriodText !== undefined ? `-${downloadPeriodText}` : ''
      const exportCsvFileName = `${business.name}-CPD-Activity${downloadPeriod}.csv`
      return { industryCpdCategorys, businessIndustryGoals, exportCsvFileName };
    }),
    withHandlers({
      exportCsv:
        ({ industryCpdCategorys, publishers, businessIndustryGoals, exportCsvFileName }) =>
        (businessActivitiesData) => {
          const columns = getUserCpdActivityColumns(
            industryCpdCategorys,
            businessIndustryGoals
          );
          const rows = mapUserCpdActivity(
            industryCpdCategorys,
            publishers,
            businessIndustryGoals
          )(
            fp.flatMap(({ user, cpdPoints }) =>
              fp.map((cpdPoint) => ({ user, cpdPoint }))(cpdPoints)
            )(businessActivitiesData)
          );
          downloadBlobStreamFile(
            writeDataToCsvStream(
              getCsvStream({ header: true, columns }),
              rows
            ).pipe(getBlobStream()),
            exportCsvFileName
          );
        }
    }),
    withStateHandlers(
      {
        isDownloadingBusinessActivities: false,
        downloadingProgress: 0
      },
      {
        toggleDownloading:
          ({ isDownloadingBusinessActivities }) =>
          () => ({
            isDownloadingBusinessActivities: !isDownloadingBusinessActivities
          }),
        setDownloadingProgress: () => (downloadingProgress) => ({
          downloadingProgress: downloadingProgress > 1 ? 1 : downloadingProgress
        })
      }
    ),
    lifecycle({
      componentDidUpdate(prevProps) {
        const {
          isDownloadingBusinessActivities,
          isFetching,
          setDownloadingProgress,
          businessActivities,
          getBusinessActivities,
          toggleDownloading,
          cleanBusinessActivities,
          exportCsv,
          business,
          yearsFromNow
        } = this.props;

        if (isDownloadingBusinessActivities && !isFetching) {
          const progress = businessActivities
            ? (businessActivities.skip + businessActivities.take) /
              businessActivities.hits
            : 0;
          setDownloadingProgress(progress);
          const fetchingCompleted = businessActivities && progress >= 1;
          if (!fetchingCompleted) {
            const filter = {
              skip: businessActivities ? businessActivities.result.length : 0,
              take: 6
            };
            getBusinessActivities(business.id, yearsFromNow, filter);
          } else {
            exportCsv(businessActivities.result);
            cleanBusinessActivities();
            toggleDownloading();
          }
        }
      }
    })
  )(DownloadButton);
};



// WEBPACK FOOTER //
// ./src/containers/pages/shared/create-download-business-activity-button.js